import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { Button, TextField } from '@finpay-development/shared-components';
import { Grid, Typography, Button as MUIButton, Box, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { AttachMoney } from '@mui/icons-material';
import moment from 'moment';
import { formatNumberToUSD, formatCurrencyInput } from 'src/shared/calculators';
import { parseAmount } from '../hub-utils';
import type { PaymentProgramViewContext } from './use-payment-program-view';

const AttachMoneyIcon = AttachMoney;

type CustomProgramChoice = 'requested' | 'min-preapproved';

interface CustomResultsViewProps {
  ctx: PaymentProgramViewContext;
}

/**
 * Custom-results step: input fields, results table, Back, Process HRE (if high risk), Add Payment Details.
 * Conforms to UX rules in use-custom-program-view.ts: program options always show calculated program;
 * option selection does not change user's affordable DP/recurring; min prompts only when entered &lt; min;
 * preauthorized (meets minimums) = green box, high risk = red box.
 */
export function CustomResultsView({ ctx }: CustomResultsViewProps) {
  const {
    isMobile,
    billingFrequency,
    showDownPayment,
    showRecurringAmount,
    customDownPayment,
    customRecurringAmount,
    downPaymentError,
    recurringAmountError,
    optDownPayment,
    recurringAmt,
    customDownPmt,
    calculatedTerm,
    calculatedTermDisplay,
    customRecurringAmtFinal,
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    minRecurringPreapprovedForPrompt,
    minTermPreapproved,
    setCustomDownPayment,
    setCustomRecurringAmount,
    setCustomTerm,
    customTerm,
    isHighRisk,
    meetsPreauthMinimums,
    patientId,
    encounterId,
    navigate,
    handleBack,
    handleSelectCustomProgram,
    handleSelectMinPreapprovedProgram,
    handleProcessHre,
    handleDownPaymentChange,
    handleRecurringAmountChange,
    handleDownPaymentBlur,
    handleRecurringAmountBlur,
    classes,
  } = ctx;

  const isWeekly = billingFrequency === 'weekly';
  const termLabel = isWeekly ? (calculatedTermDisplay > 1 ? 'weeks' : 'week') : (calculatedTermDisplay > 1 ? 'months' : 'month');
  const eachPeriod = isWeekly ? 'each week' : 'each month';
  const minTermDisplay = minTermPreapproved != null ? (isWeekly ? minTermPreapproved * 4 : minTermPreapproved) : 0;
  const minTermLabel = isWeekly ? (minTermDisplay > 1 ? 'weeks' : 'week') : (minTermDisplay > 1 ? 'months' : 'month');
  const showMinPreauthProgram =
    minDownPaymentPreapproved != null && minRecurringPreapproved != null && minTermPreapproved != null;
  // Per rules: only show minimal preauthorized program when calculated program is high risk
  const showMinPreauthRow = showMinPreauthProgram && isHighRisk;
  const showProgramOptions = showDownPayment || showRecurringAmount;

  // Green / preauth eligibility: same as meetsPreauthMinimums from use-custom-program-view (affordable-down-based recurring floor when applicable).
  const requestedProgramMeetsMinimums = meetsPreauthMinimums;

  const hasCalculatedTerm = calculatedTerm > 0;
  const [selectedProgram, setSelectedProgram] = useState<CustomProgramChoice>(() =>
    hasCalculatedTerm ? 'requested' : 'min-preapproved'
  );
  const prevHasCalculatedTermRef = useRef(hasCalculatedTerm);

  useEffect(() => {
    if (hasCalculatedTerm && !prevHasCalculatedTermRef.current) {
      setSelectedProgram('requested');
    }
    prevHasCalculatedTermRef.current = hasCalculatedTerm;
  }, [hasCalculatedTerm]);

  useEffect(() => {
    if (!showMinPreauthRow && selectedProgram === 'min-preapproved') {
      setSelectedProgram('requested');
    }
  }, [showMinPreauthRow, selectedProgram]);

  // Rule: Program calculation or option selection must not change the user's entered data in the affordable fields.
  // We do not sync form fields when the user toggles between requested and min-preapproved.

  const enteredDownPayment = parseAmount(customDownPayment);
  const enteredRecurring = parseAmount(customRecurringAmount);
  const showMinDownPaymentPrompt =
    minDownPaymentPreapproved != null &&
    !isNaN(enteredDownPayment) &&
    enteredDownPayment < minDownPaymentPreapproved;
  const showMinRecurringPrompt =
    minRecurringPreapprovedForPrompt != null &&
    !isNaN(enteredRecurring) &&
    enteredRecurring < minRecurringPreapprovedForPrompt;

  return (
    <Grid item xs={12}>
      {/* Input fields */}
      {(showDownPayment || showRecurringAmount) && (
        <Grid container className="mt-4" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" textAlign="left" sx={{ mb: 2, fontSize: '2rem', lineHeight: '2.6rem' }}>
              How close can you get to the {formatNumberToUSD(optDownPayment)} down payment and still feel comfortable?
            </Typography>
          </Grid>
          {showDownPayment && (
            <Grid item xs={12} key="down-payment-field" zeroMinWidth sx={{ minWidth: 0, overflow: 'visible' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '250px minmax(0, 1fr)',
                  gap: 2,
                  width: '100%',
                  minWidth: 0,
                  alignItems: 'start',
                }}
              >
                <Box sx={{ minWidth: 0, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                  <TextField
                    label="Affordable Down Payment"
                    name="downPayment"
                    type="text"
                    value={customDownPayment}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleDownPaymentChange(e);
                      setSelectedProgram('requested');
                    }}
                    onBlur={handleDownPaymentBlur}
                    error={!!downPaymentError}
                    helperText={downPaymentError}
                    placeholder="0.00"
                    startAdornment={<AttachMoneyIcon />}
                    test-id="custom-down-payment"
                  />
                  {downPaymentError && (
                    <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
                      {downPaymentError}
                    </Typography>
                  )}
                </Box>
                {showMinDownPaymentPrompt && (
                  <Box sx={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ color: 'error.main', width: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}
                    >
                      The minimum pre-approved down payment is {formatNumberToUSD(minDownPaymentPreapproved!)}. Can we
                      bridge that gap? (Prompt Patient Champion)
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
          {showDownPayment && showRecurringAmount && (
            <Grid item xs={12}>
              <Typography variant="body2" textAlign="left" sx={{ fontSize: '2rem', lineHeight: '2.6rem' }}>
                How close can you get to the {formatNumberToUSD(recurringAmt)} {isWeekly ? 'weekly' : 'monthly'}{' '}
                recurring payment and still feel comfortable?
              </Typography>
            </Grid>
          )}
          {showRecurringAmount && (
            <Grid item xs={12} key="recurring-amount-field" zeroMinWidth sx={{ minWidth: 0, overflow: 'visible' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '250px minmax(0, 1fr)',
                  gap: 2,
                  width: '100%',
                  minWidth: 0,
                  alignItems: 'start',
                }}
              >
                <Box sx={{ minWidth: 0, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                  <TextField
                    label="Affordable Recurring Payment"
                    name="recurringAmount"
                    type="text"
                    value={customRecurringAmount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleRecurringAmountChange(e);
                      setSelectedProgram('requested');
                    }}
                    onBlur={handleRecurringAmountBlur}
                    error={!!recurringAmountError}
                    helperText={recurringAmountError}
                    placeholder="0.00"
                    startAdornment={<AttachMoneyIcon />}
                    test-id="custom-recurring-amount"
                  />
                  {recurringAmountError && (
                    <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
                      {recurringAmountError}
                    </Typography>
                  )}
                </Box>
                {showMinRecurringPrompt && (
                  <Box sx={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ color: 'error.main', width: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}
                    >
                      The minimum pre-approved recurring payment is{' '}
                      {formatNumberToUSD(minRecurringPreapprovedForPrompt!)}. Can we
                      bridge that gap? (Prompt patient champion or use weekly payment)
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 3,
                  fontWeight: 'bold',
                  color: isHighRisk ? 'error.main' : 'inherit',
                }}
              >
                {isHighRisk ? 'High Risk Program' : 'Best Preauthorized Program'}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* Program options: select requested or minimum pre-approved (red / green) */}
      {(showDownPayment || showRecurringAmount) && showProgramOptions && (
        <Grid container className="mt-4" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Grid container direction="row" sx={{ bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider', py: 1, px: 2 }}>
            <Grid item xs={2} />
            <Grid item xs={3}>
              <Typography variant="subtitle2" color="text.secondary" textAlign="left">
                Down Payment
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="text.secondary" textAlign="center">
                Term
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="subtitle2" color="text.secondary" textAlign="right">
                Recurring Amount
              </Typography>
            </Grid>
          </Grid>
          <RadioGroup
            name="custom-program-choice"
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value as CustomProgramChoice)}
            sx={{ width: '100%' }}
          >
            {/* Requested / calculated program row – always shown */}
            <Grid
              container
              direction="row"
              alignItems="center"
              sx={{
                py: 0.25,
                px: 1,
                mb: showMinPreauthRow ? 1.5 : 0,
                border: 2,
                borderColor: requestedProgramMeetsMinimums ? 'success.main' : 'error.main',
              }}
            >
              <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel value="requested" control={<Radio />} label="" sx={{ mr: 0 }} />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2">{formatNumberToUSD(customDownPmt)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" textAlign="center">
                  {calculatedTermDisplay} {termLabel}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" textAlign="right">
                  {formatNumberToUSD(customRecurringAmtFinal)}
                </Typography>
              </Grid>
            </Grid>
            {/* Minimum pre-approved program row – always shown when rule has min values */}
            {showMinPreauthRow && (
              <Grid
                container
                direction="row"
                alignItems="center"
                sx={{
                py: 0.25,
                px: 1,
                mt: 1.5,
                  border: 2,
                  borderColor: 'success.main',
                }}
              >
                <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel value="min-preapproved" control={<Radio />} label="" sx={{ mr: 0 }} />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2">{formatNumberToUSD(minDownPaymentPreapproved!)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" textAlign="center">
                    {minTermDisplay} {minTermLabel}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" textAlign="right">
                    {formatNumberToUSD(minRecurringPreapproved!)}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </RadioGroup>
        </Grid>
      )}

      {isMobile && calculatedTerm > 0 && (
        <Grid container direction="row" justifyContent="center">
          <Typography variant="subtitle2" className={classes.fullWidth} textAlign="center">
            The first recurring payment will occur on {moment().add(1, isWeekly ? 'weeks' : 'months').format('MM/DD/YYYY')} and {eachPeriod}
            thereafter until the balance is paid in full
          </Typography>
        </Grid>
      )}

      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={2} className="mt-2">
          <MUIButton
            onClick={handleBack}
            variant="outlined"
            fullWidth
            sx={{
              backgroundColor: 'white',
              borderColor: '#e0e0e0',
              color: '#000',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd',
              },
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
          </MUIButton>
        </Grid>
        {selectedProgram === 'requested' && isHighRisk && calculatedTerm > 0 ? (
          <Grid item xs={12} md={10} className="mt-2">
            <Button
              test-id="approve-high-risk-button"
              fullWidth
              onClick={handleProcessHre}
            >
              Process HRE
            </Button>
          </Grid>
        ) : (
          <Grid item xs={12} md={10} className="mt-2">
            <Button
              onClick={
                selectedProgram === 'min-preapproved'
                  ? handleSelectMinPreapprovedProgram
                  : handleSelectCustomProgram
              }
              fullWidth
              test-id="payment-program-custom-add-payment"
            >
              Add Payment Details
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
